/**
 * Email Queue Worker
 * Manages the queue of emails to be filed in batch
 */

export interface QueuedEmail {
	itemId: string; // REST-formatted and encoded itemId for API calls
	rawItemId: string; // Original raw itemId for metadata
	conversationId: string;
	subject: string;
	senderEmail: string;
	senderName: string;
	dateTimeCreated: string;
	to: Array<{ emailAddress: string; displayName: string }>;
	cc: Array<{ emailAddress: string; displayName: string }>;
}

const QUEUE_STORAGE_KEY = "email_filing_queue";

/**
 * Convert Outlook itemId to REST format (same logic as XplanFiler.getItemRestId)
 */
function getRestFormattedItemId(itemId: string): string {
	const restId =
		Office.context.mailbox.diagnostics.hostName === "OutlookIOS"
			? itemId
			: Office.context.mailbox.convertToRestId(
					itemId,
					Office.MailboxEnums.RestVersion.v2_0
			  );
	
	return encodeURIComponent(restId);
}

/**
 * Dispatches a custom event to notify components of queue updates
 */
function notifyQueueUpdate() {
	window.dispatchEvent(new Event("queueUpdated"));
}

/**
 * Gets all queued emails from local storage
 * (using localStorage instead of sessionStorage so queue persists when switching between emails)
 */
export function getQueuedEmails(): QueuedEmail[] {
	try {
		const queueJson = localStorage.getItem(QUEUE_STORAGE_KEY);
		if (!queueJson) return [];
		
		const queue = JSON.parse(queueJson);
		
		// Filter out old queued emails that don't have rawItemId (from before the update)
		// This prevents issues with emails queued before the REST ID conversion was implemented
		const validQueue = queue.filter((email: any) => email.rawItemId);
		
		// Update storage if we filtered out any invalid entries
		if (validQueue.length !== queue.length) {
			localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(validQueue));
		}
		
		return validQueue;
	} catch (error) {
		console.error("Error reading email queue:", error);
		return [];
	}
}

/**
 * Adds the current email to the queue
 */
export function addCurrentEmailToQueue(): { success: boolean; message: string; isAlreadyQueued?: boolean } {
	try {
		if (!Office.context.mailbox || !Office.context.mailbox.item) {
			return { success: false, message: "No email is currently selected" };
		}

		const item = Office.context.mailbox.item;
		const queue = getQueuedEmails();

		// Convert itemId to REST format for storage and comparison
		const rawItemId = item.itemId;
		const restFormattedItemId = getRestFormattedItemId(rawItemId);
		
		console.log("Adding email to queue - Raw ID:", rawItemId);
		console.log("Adding email to queue - REST ID:", restFormattedItemId);
		console.log("Current queue:", queue.map(e => ({ subject: e.subject, id: e.itemId })));
		
		// Check if already in queue (compare REST-formatted IDs)
		if (queue.some(email => email.itemId === restFormattedItemId)) {
			return { 
				success: false, 
				message: "This email is already in the queue",
				isAlreadyQueued: true 
			};
		}

		// Get sender information
		const sender = item["sender"];
		const senderEmail = sender?.emailAddress || "";
		const senderName = sender?.displayName || senderEmail;

		// Create queued email object with REST-formatted itemId
		const queuedEmail: QueuedEmail = {
			itemId: restFormattedItemId, // REST-formatted for API calls
			rawItemId: rawItemId, // Raw itemId for metadata
			conversationId: item["conversationId"] || "",
			subject: item.subject || "(No Subject)",
			senderEmail: senderEmail,
			senderName: senderName,
			dateTimeCreated: item.dateTimeCreated?.toISOString() || new Date().toISOString(),
			to: item["to"] ? Array.from(item["to"]) : [],
			cc: item["cc"] ? Array.from(item["cc"]) : [],
		};

		// Add to queue
		queue.push(queuedEmail);
		localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
		notifyQueueUpdate();

		return { 
			success: true, 
			message: `Added "${queuedEmail.subject}" to queue (${queue.length} total)`
		};
	} catch (error) {
		console.error("Error adding email to queue:", error);
		return { success: false, message: "Failed to add email to queue: " + error.message };
	}
}

/**
 * Removes a specific email from the queue by itemId
 */
export function removeFromQueue(itemId: string): boolean {
	try {
		const queue = getQueuedEmails();
		const filteredQueue = queue.filter(email => email.itemId !== itemId);
		
		if (filteredQueue.length === queue.length) {
			return false; // Item not found
		}

		localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(filteredQueue));
		notifyQueueUpdate();
		return true;
	} catch (error) {
		console.error("Error removing email from queue:", error);
		return false;
	}
}

/**
 * Clears all emails from the queue
 */
export function clearQueue(): void {
	localStorage.removeItem(QUEUE_STORAGE_KEY);
	notifyQueueUpdate();
}

/**
 * Gets the count of queued emails
 */
export function getQueueCount(): number {
	return getQueuedEmails().length;
}

/**
 * Checks if a specific email is in the queue
 */
export function isInQueue(itemId: string): boolean {
	const queue = getQueuedEmails();
	return queue.some(email => email.itemId === itemId);
}
